import { integrationTesting } from '@ps/web-core-utils';
import type { WebsiteConfig } from '@ps/web-martech-cms-types';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import type { TouchpointInterface } from './types';

const touchpointSDKUrl = 'https://touchpoint-sdk.alida.com/main.js';

declare global {
  interface Window {
    Touchpoint?: TouchpointInterface;
  }
}

type Config = WebsiteConfig['touchpoint'];
interface TouchpointConfig extends Config {
  visitor?: object;
  user_attributes?: object;
  showCloseButton?: boolean;
  locale?: string;
}

export const useTouchpoint = (
  skip: boolean,
  embeddedIntegration: boolean,
  websiteConfig?: TouchpointConfig | undefined
) => {
  const [loaded, setLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const digitalChannelConfig = useSelector((rootState: RootState) =>
    select.cms.getWebsiteConfig(rootState, 'touchpoint')
  ) as TouchpointConfig | undefined;

  const {
    app_id: appId,
    pod,
    distribution_id: distributionId = '',
    visitor = {},
    user_attributes: userAttributes = {},
    showCloseButton = true,
    locale: configLocale = ''
  } = websiteConfig || digitalChannelConfig || {};

  const locale = configLocale || useSelector(select.locale.get);

  useEffect(() => {
    if (skip) return;

    if (window.Touchpoint) {
      setLoaded(true);
    } else {
      const script = document.createElement('script');
      script.src = touchpointSDKUrl;
      script.type = 'text/javascript';

      script.onload = () => {
        setLoaded(true);
      };
      script.async = true;

      document.head.appendChild(script);
    }
  }, [skip]);

  useEffect(() => {
    const { Touchpoint, addEventListener, removeEventListener } = window;
    const onTouchpointLoaded = () => {
      setIsReady(true);
    };

    if (loaded && appId && pod && Touchpoint) {
      addEventListener('TouchpointLoaded', onTouchpointLoaded);

      Touchpoint.initialize({
        settings: {
          containerStyle: {
            borderRadius: '8px',
            boxShadow:
              '0px 3px 5px 0px rgba(0,0,0,0.2), 0px 1px 18px 0px rgba(0,0,0,0.12), 0px 6px 10px 0px rgba(0,0,0,0.14)'
          },
          locale,
          showCloseButton
        },
        visitor,
        user_attributes: userAttributes,
        publisher: {
          app_id: appId,
          pod
        }
      });
    }

    return () => {
      removeEventListener('TouchpointLoaded', onTouchpointLoaded);
    };
  }, [loaded, appId, pod, locale, visitor, userAttributes, showCloseButton]);

  const show = useCallback(() => {
    if (!isReady) return;

    distributionId && window.Touchpoint?.show(distributionId);
  }, [distributionId, isReady]);

  const getEmbeddedUrl = useCallback(() => {
    if (!isReady || !window.Touchpoint || !embeddedIntegration) return;
    return integrationTesting ? 'https://test.test/' : window.Touchpoint.getEmbeddedDistURL();
  }, [embeddedIntegration, isReady]);

  return { isReady, show, getEmbeddedUrl } as const;
};
